import { useState, useEffect, useCallback, useRef } from "react";
import { Search, Clock, ChevronDown, Loader2 } from "lucide-react";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TemparioSelector - Componente per selezionare descrizioni dal tempario/catalogo
 * Integra tempario manodopera, articoli magazzino e servizi
 * Precalcola ore manodopera per ogni item selezionato
 */
export default function TemparioSelector({ 
  value, 
  onChange, 
  onSelectItem,
  placeholder = "Cerca nel catalogo...",
  className = "",
  inputClassName = "",
  showLaborHours = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch catalogo unificato - senza categorie
  const fetchCatalog = useCallback(async (search = "") => {
    setLoading(true);
    try {
      let url = `${API}/modules/catalogo?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setCatalogItems(data.items || []);
      }
    } catch (error) {
      console.error("Error fetching catalog:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) {
        fetchCatalog(searchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, fetchCatalog]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectItem = (item) => {
    // Update description
    onChange(item.descrizione);
    
    // Callback with full item data (including labor hours and cost)
    if (onSelectItem) {
      onSelectItem({
        codice: item.codice,
        descrizione: item.descrizione,
        prezzo: item.prezzo,
        costo_acquisto: item.costo_acquisto || 0,  // Costo per calcolo margine
        ore_manodopera: item.ore_manodopera || 0,
        tipo: item.tipo,
        categoria: item.categoria,
        aliquota_iva: item.aliquota_iva || 22,
        note: item.note || ""
      });
    }
    
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    onChange(val);
    if (!isOpen && val.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value || searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            if (!catalogItems.length) {
              fetchCatalog("");
            }
          }}
          placeholder={placeholder}
          className={`form-input pr-10 ${inputClassName}`}
          data-testid="tempario-selector-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
          <ChevronDown 
            className={`w-4 h-4 text-zinc-400 transition-transform cursor-pointer ${isOpen ? "rotate-180" : ""}`}
            onClick={() => setIsOpen(!isOpen)}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg max-h-72 overflow-hidden">
          {/* Results - only descriptions, no categories */}
          <div className="max-h-64 overflow-y-auto">
            {catalogItems.length > 0 ? (
              catalogItems.map((item, idx) => (
                <div
                  key={`${item.tipo}-${item.id}-${idx}`}
                  onClick={() => handleSelectItem(item)}
                  className="px-3 py-2 hover:bg-zinc-800 cursor-pointer border-b border-zinc-800/50 last:border-0"
                  data-testid={`catalog-item-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{item.descrizione}</p>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0 flex items-center gap-2">
                      {item.ore_manodopera > 0 && showLaborHours && (
                        <div className="flex items-center gap-1 text-blue-400 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>{item.ore_manodopera}h</span>
                        </div>
                      )}
                      {item.costo_acquisto > 0 && (
                        <span className="text-green-400 font-mono text-xs" title="Costo acquisto">
                          C:€{item.costo_acquisto.toFixed(2)}
                        </span>
                      )}
                      {item.prezzo > 0 && (
                        <span className="text-primary font-mono text-sm">€{item.prezzo.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : loading ? (
              <div className="p-4 text-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Caricamento...
              </div>
            ) : (
              <div className="p-4 text-center text-zinc-500">
                <Search className="w-5 h-5 mx-auto mb-2" />
                Digita per cercare
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
